import axios from "@core/plugins/axios.plugin"
import { handleServiceCall } from "@services/helpers"
import type { EnergyManagerIotAnalyse } from "@types"

/**
 * Service pour la gestion des analyses IoT de température ambiance
 */
export class EnergyManagerService {
    /**
     * Récupère les analyses IoT d'un bâtiment
     * @param buildingId - Identifiant du bâtiment
     * @param controller - Contrôleur d'annulation
     * @returns Liste des analyses IoT
     */
    async getBuildingIotAnalyses(
        buildingId: string,
        controller?: AbortController
    ): Promise<EnergyManagerIotAnalyse[]> {
        return handleServiceCall<EnergyManagerIotAnalyse[]>(async () => {
            const { data } = await axios.get(
                `/api/metier/v5/energymanager/analyse/building/${buildingId}/iot`,
                {
                    signal: controller?.signal,
                }
            )
            return data?.analyses || []
        })
    }

    /**
     * Crée une analyse IoT pour un bâtiment
     * @param params - Paramètres de création de l'analyse
     * @param params.buildingId - Identifiant du bâtiment
     * @param params.dateFrom - Date de début au format YYYY-MM-DD
     * @param params.dateTo - Date de fin au format YYYY-MM-DD
     * @param params.tempUtilisation - Température de consigne en période de présence (°C)
     * @param params.tempNoUtilisation - Température de consigne en période d'absence (°C)
     * @param controller - Contrôleur d'annulation
     * @returns Identifiant de la tâche et nom de l'événement pusher
     */
    async createIotAnalyse(
        {
            buildingId,
            dateFrom,
            dateTo,
            tempUtilisation,
            tempNoUtilisation,
        }: {
            buildingId: string
            dateFrom: string
            dateTo: string
            tempUtilisation: number
            tempNoUtilisation: number
        },
        controller?: AbortController
    ): Promise<{ task_id: string; event_name: string }> {
        return handleServiceCall<{ task_id: string; event_name: string }>(
            async () => {
                const event_name = `analyse.iot.${buildingId}.${crypto.randomUUID()}`
                const { data } = await axios.post(
                    `/api/metier/v5/energymanager/analyse/building/${buildingId}/iot`,
                    {
                        date_from: dateFrom,
                        date_to: dateTo,
                        temperature_utilisation: tempUtilisation,
                        temperature_nonutilisation: tempNoUtilisation,
                        event_name,
                    },
                    {
                        signal: controller?.signal,
                    }
                )

                // Réponse API : { status, task_id, event_name }
                const taskId = data?.task_id ?? data?.data?.task_id ?? ""
                const returnedEventName =
                    data?.event_name ?? data?.data?.event_name ?? event_name

                return {
                    task_id: taskId,
                    event_name: returnedEventName,
                }
            }
        )
    }

    /**
     * Génère le rapport PDF d'une analyse IoT
     * @param analyseId - Identifiant de l'analyse
     * @param controller - Contrôleur d'annulation
     * @returns Objet contenant task_id et event_name pour suivre la tâche
     */
    async generateIotAnalyseReport(
        analyseId: string,
        controller?: AbortController
    ) {
        return handleServiceCall<{
            task_id: string
            event_name: string
        }>(async () => {
            const event_name = `analyse.iot.rapport.${analyseId}.${crypto.randomUUID()}`

            const { data } = await axios.post(
                `/api/metier/v5/analyse/${analyseId}/rapport`,
                { event_name },
                {
                    signal: controller?.signal,
                }
            )

            // Réponse API : { status, task_id, event_name }
            const taskId = data?.task_id ?? data?.data?.task_id ?? ""
            const returnedEventName =
                data?.event_name ?? data?.data?.event_name ?? event_name

            return {
                task_id: taskId,
                event_name: returnedEventName,
            }
        })
    }
}
